import { QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { BrowserRouter, Link, Route, Routes } from 'react-router';
import { EmptyState } from '../components/States.tsx';
import { Button } from '../components/ui/button.tsx';
import { SecurityPage } from '../features/auth/SecurityPage.tsx';
import { LoginPage } from '../features/auth/LoginPage.tsx';
import { ClientsPage } from '../features/clients/ClientsPage.tsx';
import { FunnelPage } from '../features/funnel/FunnelPage.tsx';
import { MarketingPage } from '../features/marketing/MarketingPage.tsx';
import { OverviewPage } from '../features/overview/OverviewPage.tsx';
import { StatusPage } from '../features/status/StatusPage.tsx';
import { SalespersonPage } from '../features/team/SalespersonPage.tsx';
import { TeamPage } from '../features/team/TeamPage.tsx';
import { I18nProvider, useT } from '../lib/i18n.tsx';
import { createQueryClient } from '../lib/query-client.ts';
import { useMe } from '../lib/queries.ts';
import { AppShell } from './AppShell.tsx';
import { RequireAuth, RequirePermission, useSessionExpiry } from './auth.tsx';

function NotFound() {
  const t = useT();
  return (
    <EmptyState
      title={t.errors.notFoundTitle}
      description={t.errors.notFoundDetail}
      action={
        <Button asChild variant="outline">
          <Link to="/">{t.errors.backHome}</Link>
        </Button>
      }
    />
  );
}

/** The routes, without a router: tests wrap them in a MemoryRouter. */
export function AppRoutes() {
  useSessionExpiry();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="security" element={<SecurityPage />} />
        <Route element={<RequirePermission permission="dashboard.overview" />}>
          <Route index element={<OverviewPage />} />
        </Route>
        <Route element={<RequirePermission permission="dashboard.funnel" />}>
          <Route path="funnel" element={<FunnelPage />} />
        </Route>
        <Route element={<RequirePermission permission="dashboard.team" />}>
          <Route path="team" element={<TeamPage />} />
          <Route path="team/:id" element={<SalespersonPage />} />
        </Route>
        <Route element={<RequirePermission permission="customers.view_names" />}>
          <Route path="clients" element={<ClientsPage />} />
        </Route>
        <Route element={<RequirePermission permission="dashboard.marketing" />}>
          <Route path="marketing" element={<MarketingPage />} />
        </Route>
        <Route element={<RequirePermission permission="data_quality.view" />}>
          <Route path="status" element={<StatusPage />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** The signed-in user's locale picks the catalog; Uzbek (Latin) until we know it. */
export function Localized() {
  const me = useMe();
  return (
    <I18nProvider locale={me.data?.locale ?? 'uz-Latn'}>
      <AppRoutes />
    </I18nProvider>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Localized />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
